'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type DeepPartialSkipArrayKey,
  type Resolver,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form'

import { useWidgetConfigSync } from '@/features/dashboard/hooks/use-widget-config-sync'
import { getEffectivePricePrecision } from '@/features/dashboard/lib/orderbook-data'
import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { orderFormValuesStorageKey } from '@/features/order/hooks/use-order-form-values-persist'
import {
  formatNumberWithCommas,
  formatNumberWithPrecision,
  getPrecisionFromStep,
  isValidNumberInput,
  parseFormattedNumber,
} from '@/features/order/lib/number-format-util'
import { useInstrumentMap } from '@/features/order/order-form/hooks/use-instrument-map'
import { useOptimisticOrderClear } from '@/features/order/order-form/hooks/use-optimistic-order-clear'
import { useOrderFormAccountResolution } from '@/features/order/order-form/hooks/use-order-form-account-resolution'
import { useOrderFormCaps } from '@/features/order/order-form/hooks/use-order-form-caps'
import { useOrderFormTradeVenue } from '@/features/order/order-form/hooks/use-order-form-trade-venue'
import { shouldCopyAtomAmount } from '@/features/order/order-form/lib/atom-amount-sync'
import {
  buildLimitChasePresetPayload,
  buildManualPresetPayload,
  buildTwapPresetPayload,
  FormItemRaw,
} from '@/features/order/order-form/lib/build-preset-payload'
import { isExitActive, withoutExitBracket } from '@/features/order/order-form/lib/exit-active'
import {
  defaultInstrumentAmountUnit,
  resolveBaseCurrency,
  resolveQuoteCurrency,
} from '@/features/order/order-form/lib/instrument-resolution'
import { lockAfterEdit, mergeRowAmount } from '@/features/order/order-form/lib/locked-amount'
import { normalizeAccountScopedItems } from '@/features/order/order-form/lib/normalize-account-filter'
import {
  isSameOrderItem,
  isSameVisualOrderItem,
  preserveCurrentItemOrder,
} from '@/features/order/order-form/lib/order-item-order'
import {
  ORDER_ROWS_CONFIG_KEY,
  serializeOrderRows,
} from '@/features/order/order-form/lib/persisted-order-rows'
import { buildPriceSyncPayloads } from '@/features/order/order-form/lib/price-sync-payloads'
import { pickReferencePrice } from '@/features/order/order-form/lib/resolve-sync-price'
import { getRowGroupKey } from '@/features/order/order-form/lib/row-group-key'
import { useIsSelectionWriter } from '@/features/order/order-form/lib/selection-writer'
import {
  buildSingleHedgeTabParams,
  singleHedgeContext,
} from '@/features/order/order-form/lib/single-hedge-tab'
import { submitAdvancedTwapOrder as submitAdvancedTwap } from '@/features/order/order-form/lib/submitters/advanced-twap'
import { submitLimitChaseOrder as submitLimitChase } from '@/features/order/order-form/lib/submitters/limit-chase'
import { submitLimitMarketOrder as submitLimitMarket } from '@/features/order/order-form/lib/submitters/limit-market'
import { submitScaleOrder as submitScale } from '@/features/order/order-form/lib/submitters/scale'
import {
  type SingleHedgeSubmission,
  type SingleHedgeTarget,
  submitSingleHedgeOrder as submitSingleHedge,
} from '@/features/order/order-form/lib/submitters/single-hedge'
import { submitSpreaderOrder as submitSpreader } from '@/features/order/order-form/lib/submitters/spreader'
import { submitTriggerAlgoOrder as submitTriggerAlgo } from '@/features/order/order-form/lib/submitters/trigger-algo'
import { submitTwapOrder as submitTwap } from '@/features/order/order-form/lib/submitters/twap'
import type {
  RawOrderFormValues,
  SubmitterContext,
} from '@/features/order/order-form/lib/submitters/types'
import { hasTriggerData, isTriggerActive } from '@/features/order/order-form/lib/trigger-active'
import {
  getUnifiedOrderMode,
  UnifiedItemValues,
  unifiedOrderFormSchema,
  UnifiedOrderFormValues,
  UnifiedOrderMode,
} from '@/features/order/order-form/lib/unified-order-schema'
import { findUnresolvedSubmissionIndexes } from '@/features/order/order-form/lib/validate-submission-rows'
import { SCALE_FORM_DEFAULTS } from '@/features/scale/lib/build-scale-params'
import { buildSpreaderSelectedInstrument } from '@/features/spreader/lib/build-selected-instrument'
import { canActivateSpreaderSelection } from '@/features/spreader/lib/spreader-instrument-selection'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import { parseExchangeField } from '@/lib/proto/util'
import { toast } from '@/lib/toast'
import { formatSymbol } from '@/lib/utils/format-util'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { useUserAccounts } from '@/service/account.hook'
import {
  useStartAdvancedTwapAlgo,
  useStartLimitChaseAlgo,
  useStartLimitChaseAlgoBatch,
  useStartScaleAlgo,
  useStartSingleHedgeAlgo,
  useStartSpreaderAlgo,
  useStartTwapAlgo,
  useStartTwapAlgoBatch,
} from '@/service/algo.hook'
import { useConfirmExecutionPlan } from '@/service/execution-plan.hook'
import { useCreateOrderBatch } from '@/service/execution.hook'
import { useVenueCapsLookup } from '@/service/venue-capabilities.hook'
import { useMultiMarketDataStream } from '@/service/ws/use-market-data-stream'
import { allPositionsAtom } from '@/state/account'
import {
  findTickerBySymbol,
  scheduleTickerMapWrite,
  watchListTickerMapAtom,
} from '@/state/dashboard'
import {
  markOrderFormManual,
  type OrderFormCollapsibleConfig,
  orderFormCollapsibleConfigAtomFamily,
  type OrderFormDenomPreference,
  orderFormDenomPreferenceAtomFamily,
  orderFormExecutedCountAtomFamily,
  orderFormPopupCardIdAtom,
  orderFormPopupConfigAtom,
  OrderFormSummaryTab,
  orderFormSummaryTabAtomFamily,
  singleHedgeTabAtomFamily,
  spreaderDismissedAtomFamily,
  spreaderSelectedAtomFamily,
} from '@/state/order'
import { SpreaderMatrixInstrument } from '@/state/widget-instances'
import { SelectedInstrument, SelectedOption } from '@/types/dashboard'
import { AlgoType } from '@/types/generated/algo'
import { PresetPayload } from '@/types/generated/preset'

// Default amountUnit for an option item (options always use base denomination)
function defaultOptionAmountUnit(instrumentName: string): string {
  return resolveBaseCurrency([], instrumentName)
}

// Helper to convert selectedOptions to unified items
function optionsToItems(options: SelectedOption[]): UnifiedItemValues[] {
  return options.map((opt, idx) => ({
    id: `opt-${idx}-${opt.instrumentName}`,
    symbol: opt.instrumentName,
    side: opt.tradeSide,
    amount: opt.amount ?? '',
    amountUnit: defaultOptionAmountUnit(opt.instrumentName),
    itemType: 'option' as const,
    accountId: opt.accountId,
    accountBinding: opt.accountBinding,
    // Option-chain selections carry their venue on the live ticker. Preserve it so
    // multi-exchange forms resolve qtyPrecision/tickSize from this row's own venue.
    exchange: opt.exchange ?? (opt.ticker ? String(opt.ticker.exchange) : undefined),
    minTradeAmount: opt.minTradeAmount,
    spreaderRole: opt.spreaderRole,
    lockedAmount: opt.lockedAmount,
    optionData: {
      instrumentName: opt.instrumentName,
      askBidSide: opt.side,
      price: opt.price,
      ticker: opt.ticker,
    },
  }))
}

// Helper to convert selectedInstruments to unified items
function instrumentsToItems(
  instruments: SelectedInstrument[],
  preference: OrderFormDenomPreference
): UnifiedItemValues[] {
  return instruments.map((inst, idx) => ({
    id: `inst-${idx}-${inst.symbol}`,
    symbol: inst.symbol,
    side: inst.side,
    amount: inst.amount ?? '',
    amountUnit: defaultInstrumentAmountUnit(inst, preference),
    itemType: 'instrument' as const,
    accountId: inst.accountId,
    accountBinding: inst.accountBinding,
    baseCurrency: inst.baseCurrency,
    quoteCurrency: inst.quoteCurrency,
    instrumentKind: inst.instrumentKind,
    lastPrice: inst.lastPrice,
    exchange: inst.exchange,
    exchangeSymbol: inst.exchangeSymbol,
    exchangeId: inst.exchangeId,
    internalId: inst.internalId,
    settlementCurrency: inst.settlementCurrency,
    tickSize: inst.tickSize,
    minTradeAmount: inst.minTradeAmount,
    contractSize: inst.contractSize,
    pricePrecision: inst.pricePrecision,
    qtyPrecision: inst.qtyPrecision,
    expirationTsMs: inst.expirationTsMs,
    fundingInterval: inst.fundingInterval,
    spreaderRole: inst.spreaderRole,
    lockedAmount: inst.lockedAmount,
  }))
}

// Same identity criteria as isSameOrderItem below (itemType + symbol + accountId), as a string
// key — used for the spreader role-assignment Maps in activateSpreader, since the same symbol
// can appear as two independent rows on different accounts.
// How many of a leg's form→atom amount writes to remember as echoes (see formEchoAmountsRef).
const FORM_ECHO_HISTORY = 8

function legKey(itemType: 'instrument' | 'option', symbol: string, accountId?: string) {
  return `${itemType}:${symbol}:${accountId ?? ''}`
}

interface UseUnifiedOrderFormProps {
  cardId: string
  selectedOptions: SelectedOption[]
  setSelectedOptions: (
    options: SelectedOption[] | ((prev: SelectedOption[]) => SelectedOption[])
  ) => void
  selectedInstruments: SelectedInstrument[]
  setSelectedInstruments: (
    instruments: SelectedInstrument[] | ((prev: SelectedInstrument[]) => SelectedInstrument[])
  ) => void
  selectedSymbol?: { id: string; symbol: string; type: string; lastPrice?: string } | null
}

export function useUnifiedOrderForm({
  cardId,
  selectedOptions,
  setSelectedOptions,
  selectedInstruments,
  setSelectedInstruments,
  selectedSymbol,
}: UseUnifiedOrderFormProps) {
  const t = useTranslations('orderForm')
  // ========== 최상단: Atoms, State, Refs, Memoized Values ==========
  // Atom values
  const [selectedTab, setSelectedTab] = useAtom(orderFormSummaryTabAtomFamily(cardId))
  const isSingleHedgeMode = selectedTab === OrderFormSummaryTab.SingleHedge
  // tickerMap is only ever read through tickerMapRef (syncPrice) — subscribe via
  // store.sub below instead of useAtomValue. A hook-level subscription would
  // re-render this hook's owner (OrderFormProvider → the whole form tree via
  // context) on every ticker tick of every streamed symbol.
  const store = useStore()
  // The form's own venue (its ticket's) — never another widget's focused tab, so switching
  // a chart tab or the chart's account doesn't re-point the form (useOrderFormTradeVenue).
  const cardTradeVenue = useOrderFormTradeVenue(cardId)
  const { data: userAccountsData } = useUserAccounts()
  // Ref so the submit callbacks' dependency lists (and identities) don't change
  // just because accounts refetched; only read at submit time for analytics.
  const analyticsAccountsRef = useRef(userAccountsData?.accounts ?? [])
  analyticsAccountsRef.current = userAccountsData?.accounts ?? []
  const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  const primaryExchangeRef = useRef(primaryExchange)
  primaryExchangeRef.current = primaryExchange
  const instrumentMap = useInstrumentMap(primaryExchange)
  const instruments = useMemo(() => Array.from(instrumentMap.values()), [instrumentMap])
  // Spreader legs can span exchanges other than the ticket's primary one (e.g. a Deribit
  // option leg quoting a Hyperliquid perp) — instrumentMap above only covers primaryExchange,
  // so submitSpreaderOrder resolves each leg's contractSize/baseCurrency against its OWN
  // exchange via this instead. Cached/shared with spreader-matrix-widget.tsx's own asset
  // picker, not a new per-mount fetch.
  const multiExchangeInstrumentMap = useMultiExchangeInstrumentMap()
  const multiExchangeInstrumentMapRef = useRef(multiExchangeInstrumentMap)
  multiExchangeInstrumentMapRef.current = multiExchangeInstrumentMap
  const popupCardId = useAtomValue(orderFormPopupCardIdAtom)
  const setPopupCardId = useSetAtom(orderFormPopupCardIdAtom)
  const setPopupConfig = useSetAtom(orderFormPopupConfigAtom)
  const collapsibleConfigAtom = useMemo(
    () => orderFormCollapsibleConfigAtomFamily(cardId),
    [cardId]
  )
  // Entry/Exit input changes replace the full config object on every character.
  // This large hook only needs reactive routing flags; submit/preset callbacks
  // read the latest full config on demand. That keeps draft values from
  // broadcasting a new Provider value through the whole order form.
  const collapsibleStatusAtom = useMemo(
    () =>
      selectAtom(
        collapsibleConfigAtom,
        (config) => ({
          hasTrigger: hasTriggerData(config) && isTriggerActive(config),
          hasExit: isExitActive(withoutExitBracket(config)),
        }),
        (a, b) => a.hasTrigger === b.hasTrigger && a.hasExit === b.hasExit
      ),
    [collapsibleConfigAtom]
  )
  const collapsibleStatus = useAtomValue(collapsibleStatusAtom)
  const getCollapsibleConfig = useCallback(
    (): OrderFormCollapsibleConfig => withoutExitBracket(store.get(collapsibleConfigAtom)),
    [collapsibleConfigAtom, store]
  )

  const {
    orderAccounts,
    accountExchange,
    resolveInheritedAccountId,
    resolveItemAccountId,
    setOrderAccountFilter: setOrderAccountFilterRaw,
  } = useOrderFormAccountResolution({
    accounts: userAccountsData?.accounts,
    primaryExchange,
    // No card-account tier: the Order Form has no account of its own to inherit, and the
    // linking group's focused-tab account belongs to the chart. An inherited row resolves
    // form override → scoped peer → remembered → venue default.
    cardTradingAccountId: undefined,
    selectedInstruments,
    selectedOptions,
    setSelectedInstruments,
    setSelectedOptions,
  })
  // Picking an account tab is an edit of the ticket (see markOrderFormManual).
  const setOrderAccountFilter = useCallback(
    (...args: Parameters<typeof setOrderAccountFilterRaw>) => {
      markOrderFormManual(cardIdRef.current)
      setOrderAccountFilterRaw(...args)
    },
    [setOrderAccountFilterRaw]
  )

  // Venue-native TP/SL: every row's venue must support it (the card caps are the intersection).
  const orderFormCaps = useOrderFormCaps(cardId)
  const lookupVenueCaps = useVenueCapsLookup()
  const supportsTriggerOrders = useMemo(() => {
    const hasRows = selectedInstruments.length + selectedOptions.length > 0
    // A hedge needs no row: with none it runs under the card's own account.
    if (!hasRows && isSingleHedgeMode) {
      const accountId = resolveInheritedAccountId(primaryExchange)
      const hedgeVenue = orderAccounts.find(
        (account) => account.accountId === accountId
      )?.tradeVenue
      return lookupVenueCaps(hedgeVenue).caps.venueTriggers
    }
    return hasRows && orderFormCaps.caps.venueTriggers
  }, [
    orderAccounts,
    selectedInstruments,
    selectedOptions,
    isSingleHedgeMode,
    resolveInheritedAccountId,
    primaryExchange,
    orderFormCaps,
    lookupVenueCaps,
  ])

  const { syncConfig } = useWidgetConfigSync()

  // Local state
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Refs
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasSyncedRef = useRef(false)
  // Tracks genuine user edits, separate from react-hook-form's own isDirty —
  // the server-restore effect below (order-form-context.tsx) intentionally
  // writes restored amounts with shouldDirty:true, so isDirty alone can't
  // tell "you have unsaved input" apart from "we just restored your saved
  // config". Only the handlers below set this; resetFormAfterOrder and the
  // ticker-change reset clear it. Read by runtime-recovery to decide whether
  // this card blocks a Level 3 reload — see reload-blocker-registry.ts.
  const userEditedRef = useRef(false)
  // Every user edit also makes the ticket manual: a seeded row stops following the group's tab.
  const cardIdRef = useRef(cardId)
  cardIdRef.current = cardId
  const prevSymbolIdRef = useRef<string | null>(null)
  const tickerMapRef = useRef(store.get(watchListTickerMapAtom))
  // syncPrice용 stable refs — 렌더마다 인라인 업데이트하므로 별도 effect 불필요
  const selectedOptionsRef = useRef(selectedOptions)
  selectedOptionsRef.current = selectedOptions
  const selectedInstrumentsRef = useRef(selectedInstruments)
  selectedInstrumentsRef.current = selectedInstruments
  const selectedSymbolRef = useRef(selectedSymbol)
  selectedSymbolRef.current = selectedSymbol

  // Computed values
  // Visual-row identity includes accountId (see row-group-key.ts), so the same
  // ticker on two accounts is two independently editable/submittable rows.
  // `mode`/single-vs-multi-leg UI counts those distinct rows. primaryExchange is the same
  // fallback used everywhere else in this hook for resolving an item's own
  // exchange when it doesn't carry one explicitly.
  const visualRowGroupKeys = useMemo(() => {
    const keys = new Set<string>()
    selectedInstruments.forEach((inst) =>
      keys.add(
        getRowGroupKey(
          {
            accountId: inst.accountId,
            exchange: inst.exchange,
            itemType: 'instrument',
            symbol: inst.symbol,
          },
          primaryExchange
        )
      )
    )
    selectedOptions.forEach((opt) =>
      keys.add(
        getRowGroupKey(
          {
            accountId: opt.accountId,
            exchange: opt.exchange ?? (opt.ticker ? String(opt.ticker.exchange) : undefined),
            itemType: 'option',
            symbol: opt.instrumentName,
          },
          primaryExchange
        )
      )
    )
    return keys
  }, [selectedInstruments, selectedOptions, primaryExchange])

  const visualRowCount = visualRowGroupKeys.size
  const isSingleVisualRow = visualRowCount === 1

  // Only meaningful (and only computed) when there's exactly one visual row —
  // a multi-ticker basket has no single "the row" to classify by account/side.
  // isSplitAccountRow: that one row has 2+ member accounts.
  // hasMixedSides: those members include both buy and sell — side-dependent
  // features (TP/SL, exit bracket, primarySide) must gate on THIS, not on
  // isSplitAccountRow alone, since a same-side split is still safe for them.
  const singleRowMemberSides = useMemo(() => {
    if (!isSingleVisualRow) return []
    return [
      ...selectedInstruments.map((inst) => inst.side),
      ...selectedOptions.map((opt) => opt.tradeSide),
    ]
  }, [isSingleVisualRow, selectedInstruments, selectedOptions])
  const isSplitAccountRow = isSingleVisualRow && singleRowMemberSides.length >= 2
  const hasMixedSides = isSingleVisualRow && new Set(singleRowMemberSides).size > 1

  const mode: UnifiedOrderMode = useMemo(
    () => getUnifiedOrderMode(visualRowCount),
    [visualRowCount]
  )

  const isTwapMode = selectedTab === OrderFormSummaryTab.TWAP
  const isAdvancedTwapMode = selectedTab === OrderFormSummaryTab.AdvancedTwap
  const isScaleMode = selectedTab === OrderFormSummaryTab.Scale
  const isLimitChaseMode = selectedTab === OrderFormSummaryTab.LimitChase
  const isMarketOrder = selectedTab === OrderFormSummaryTab.Market
  // Not driven by selectedTab (unlike the flags above) — a spread-matrix widget cell click
  // tags the instruments it adds with a role directly, and their presence alone is what
  // makes spreader mode available, regardless of whichever order-type tab was last active.
  // Requires 2+ legs with exactly one quoting leg present — validateSpreaderLegs (submit
  // time) is the strict "exactly one quoting, at least one taking" check; this gate only
  // needs "at least one of each" so an in-progress N-leg build (e.g. before the just-added
  // leg via the "+ Add leg" picker has been assigned a role) doesn't flicker out of
  // spreader mode. A leg count dropping below 2, or losing all quoting/taking tags, falls
  // back to a normal order type (see the reset effect below).
  const hasSpreaderLegs = useMemo(() => {
    const legs = [...selectedInstruments, ...selectedOptions]
    return (
      legs.length >= 2 &&
      legs.some((l) => l.spreaderRole === 'quoting') &&
      legs.some((l) => l.spreaderRole === 'taking')
    )
  }, [selectedInstruments, selectedOptions])
  // Picking a normal Pro-dropdown tab while spreader-eligible dismisses spreader mode
  // WITHOUT touching the legs/tags (see tab-form-type.tsx's selectTab), so the user can
  // switch back via the dropdown's "Spreader" item instead of losing it for good.
  const [spreaderDismissed, setSpreaderDismissed] = useAtom(spreaderDismissedAtomFamily(cardId))
  const [spreaderSelected, setSpreaderSelected] = useAtom(spreaderSelectedAtomFamily(cardId))
  const isSpreaderMode =
    (spreaderSelected || hasSpreaderLegs) &&
    !spreaderDismissed &&
    orderFormCaps.caps.supportedAlgos.includes(AlgoType.ALGO_TYPE_SPREADER)
  // A fresh pair of quoting/taking legs (new spread-matrix click) should show the spreader
  // ticket immediately, not stay dismissed from whatever was previously active.
  const prevHasSpreaderLegsRef = useRef(hasSpreaderLegs)
  useEffect(() => {
    if (hasSpreaderLegs && !prevHasSpreaderLegsRef.current && spreaderDismissed) {
      setSpreaderDismissed(false)
    }
    // Matrix-seeded tickets still fall back to Limit when their valid role pair breaks.
    // An explicitly selected Spreader ticket stays open, including with zero legs, so the
    // user can remove/rebuild its legs through the picker without leaving the mode.
    if (!hasSpreaderLegs && prevHasSpreaderLegsRef.current && !spreaderSelected) {
      setSelectedTab(OrderFormSummaryTab.Limit)
    }
    prevHasSpreaderLegsRef.current = hasSpreaderLegs
  }, [hasSpreaderLegs, spreaderDismissed, spreaderSelected, setSpreaderDismissed, setSelectedTab])

  // Any two effective legs are Spreader-eligible before either one is tagged. The service
  // intentionally supports cross-ticker and same-side ratios; role assignment, not base
  // currency or side, defines quoting vs taking. The sole exclusion is an identical
  // `(account, symbol)` pair, which the server would coalesce into one effective leg.
  const canActivateSpreader = useMemo(() => {
    const legs = [...selectedInstruments, ...selectedOptions]
    return canActivateSpreaderSelection(legs)
  }, [selectedInstruments, selectedOptions])

  // Form setup
  const form = useForm<UnifiedOrderFormValues>({
    // The schema's .default()s make its input type looser than UnifiedOrderFormValues;
    // the resolver still produces (and the form holds) the parsed output type.
    resolver: zodResolver(unifiedOrderFormSchema) as Resolver<UnifiedOrderFormValues>,
    defaultValues: {
      items: [],
      price: '',
      priceAnchor: 'price',
      priceOffsetUnit: 'quote_value',
      twapIntervalMinutes: '',
      twapFrequencySeconds: '1',
      twapFrequencyMinSeconds: '',
      twapFrequencyMaxSeconds: '',
      twapFrequencyMode: 'fixed',
      twapOrderSize: '',
      twapOrderSizeMin: '',
      twapOrderSizeMax: '',
      twapOrderSizeMode: 'fixed',
      advancedTwapDurationSeconds: '',
      advancedTwapTotalSlices: '',
      advancedTwapBandEnabled: false,
      advancedTwapBandPct: '',
      advancedTwapReduceOnly: false,
      ...SCALE_FORM_DEFAULTS,
      entryConditionOperator: 'equal_to',
    },
  })

  const { fields, append, replace, update } = useFieldArray({
    control: form.control,
    name: 'items',
    keyName: 'fieldId',
  })

  // Amounts the account-normalization effect below copied FROM the form INTO the selection
  // atom, keyed itemType:symbol. The atom→form sync further down must never copy one of these
  // back: it is the form's own value making a round trip, and with the unit-less atom holding
  // a USD figure on one pass and an ETH figure on the next, that round trip swapped the form
  // between 100 and 0.03656 every render until React bailed ("Maximum update depth").
  // A short history per leg, not just the latest: the atom→form effect reads the atom one
  // render behind, so the echo it sees is the value written on the PREVIOUS pass.
  const formEchoAmountsRef = useRef(new Map<string, string[]>())
  const recordFormEcho = (key: string, amount: string | undefined) => {
    if (!amount) return
    const history = formEchoAmountsRef.current.get(key) ?? []
    if (history[history.length - 1] === amount) return
    formEchoAmountsRef.current.set(key, [...history, amount].slice(-FORM_ECHO_HISTORY))
  }

  // Ensures every INHERITED row without a valid account receives the exchange
  // fallback, while a SCOPED row's own accountId is only ever validated, never
  // rewritten (resolveItemAccountId handles that branch). A Personal and Spam row
  // on Deribit must remain two independently-submittable rows. No filter/override
  // state is written here — orderAccountOverrides holds only the user's own
  // AccountTab clicks, and every default is re-derived live by resolveItemAccountId
  // on each call, so there is nothing to self-heal.
  //
  // Only ONE mounted instance per card runs this (see useIsSelectionWriter): it copies this
  // instance's own form amount into the shared atom, so two instances (widget + PIP, widget +
  // sheet) holding different amounts would overwrite each other on every render.
  const isSelectionWriter = useIsSelectionWriter(cardId)
  useEffect(() => {
    if (!isSelectionWriter) return
    const instrumentExchange = (instrument: SelectedInstrument) =>
      parseExchangeField(instrument.exchange, primaryExchange)
    const { items: nextInstruments, changed: instrumentsChanged } = normalizeAccountScopedItems(
      selectedInstruments,
      (instrument) =>
        `${instrumentExchange(instrument)}:${instrument.accountId ?? ''}:${instrument.symbol}`,
      instrumentExchange,
      resolveItemAccountId,
      (instrument, exchange) => {
        if (instrument.lockedAmount) return undefined
        const rhfItems = form.getValues('items')
        const match = rhfItems.find(
          (rhfItem) =>
            rhfItem.itemType !== 'option' &&
            rhfItem.symbol === instrument.symbol &&
            (rhfItem.accountId ?? '') === (instrument.accountId ?? '') &&
            parseExchangeField(rhfItem.exchange, primaryExchange) === exchange
        )
        // Ordinary rows keep amountUnit in RHF — SelectedInstrument has no such field
        // (instrumentsToItems always re-derives amountUnit fresh via
        // defaultInstrumentAmountUnit, never reads it back off the jotai item), so
        // merging it here only ever created a phantom diff against `undefined`
        // that never converged and kept this effect re-firing.
        if (!match) return undefined
        recordFormEcho(`instrument:${instrument.symbol}`, match.amount)
        return { amount: match.amount }
      }
    )
    if (instrumentsChanged) setSelectedInstruments(nextInstruments)

    const optionExchange = (option: SelectedOption) =>
      parseExchangeField(
        option.exchange ?? (option.ticker ? String(option.ticker.exchange) : undefined),
        primaryExchange
      )
    const { items: nextOptions, changed: optionsChanged } = normalizeAccountScopedItems(
      selectedOptions,
      (option) => `${optionExchange(option)}:${option.accountId ?? ''}:${option.instrumentName}`,
      optionExchange,
      resolveItemAccountId,
      (option, exchange) => {
        if (option.lockedAmount) return undefined
        const rhfItems = form.getValues('items')
        const match = rhfItems.find(
          (rhfItem) =>
            rhfItem.itemType === 'option' &&
            rhfItem.symbol === option.instrumentName &&
            (rhfItem.accountId ?? '') === (option.accountId ?? '') &&
            parseExchangeField(rhfItem.exchange, primaryExchange) === exchange
        )
        if (!match) return undefined
        recordFormEcho(`option:${option.instrumentName}`, match.amount)
        return { amount: match.amount }
      }
    )
    if (optionsChanged) setSelectedOptions(nextOptions)
  }, [
    form,
    isSelectionWriter,
    primaryExchange,
    resolveItemAccountId,
    selectedInstruments,
    selectedOptions,
    setSelectedInstruments,
    setSelectedOptions,
  ])

  const { triggerOptimistic, warnIfVenueDown } = useOptimisticOrderClear(
    cardId,
    form,
    setSelectedInstruments,
    setSelectedOptions
  )

  // ========== React Query Mutations & Handlers ==========
  // REST API mutation (batch)
  const { mutate: createOrderBatch } = useCreateOrderBatch()

  // TWAP mutations
  const { mutateAsync: startTwap } = useStartTwapAlgo()
  const { mutateAsync: startTwapBatch } = useStartTwapAlgoBatch()

  // Advanced TWAP mutation — single instrument/account, no batch variant.
  const { mutateAsync: startAdvancedTwap } = useStartAdvancedTwapAlgo()

  // Scale mutation — single instrument/account, no batch variant.
  const { mutateAsync: startScale } = useStartScaleAlgo()

  // LimitChase mutations
  const { mutateAsync: startLimitChase } = useStartLimitChaseAlgo()
  const { mutateAsync: startLimitChaseBatch } = useStartLimitChaseAlgoBatch()

  // Trigger algo mutation
  const { mutateAsync: confirmPlan } = useConfirmExecutionPlan()

  // Spreader mutation
  const { mutateAsync: startSpreader } = useStartSpreaderAlgo()

  // Single hedge mutation (Hedge tab)
  const { mutateAsync: startSingleHedge } = useStartSingleHedgeAlgo()

  // Clear timeout helper
  const clearSubmitTimeout = useCallback(() => {
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current)
      submitTimeoutRef.current = null
    }
  }, [])

  const syncPrice = useCallback(() => {
    // Don't overwrite the offset value when using a relative anchor
    if ((form.getValues('priceAnchor') ?? 'price') !== 'price') return

    const opts = selectedOptionsRef.current
    const insts = selectedInstrumentsRef.current

    if (opts.length === 0 && insts.length === 0) {
      form.setValue('price', '', { shouldValidate: false })
      hasSyncedRef.current = false
      return
    }
    if (opts.length > 0) {
      const option = opts[0]
      // tickerMapRef is keyed by `exchange:symbol` (watchlistEntryKey) — a plain
      // bracket lookup by symbol never matches, so this must go through the
      // suffix-matching helper.
      const tickerData = findTickerBySymbol(tickerMapRef.current, option.instrumentName)
      // Convention: buy fills at best ask, sell fills at best bid.
      const sidePrice =
        option.tradeSide === 'sell'
          ? (option.ticker?.bestBidPrice ?? tickerData?.bestBidPrice)
          : (option.ticker?.bestAskPrice ?? tickerData?.bestAskPrice)
      const resolvedPrice = pickReferencePrice(
        sidePrice,
        option.ticker?.markPrice,
        tickerData?.markPrice,
        tickerData?.lastPrice,
        option.price
      )
      if (resolvedPrice) {
        form.setValue('price', resolvedPrice, { shouldValidate: true })
        hasSyncedRef.current = true
      }
      return
    }

    if (insts.length > 0) {
      const instrument = insts[0]
      const tickerData = findTickerBySymbol(tickerMapRef.current, instrument.symbol)
      // Convention: buy fills at best ask, sell fills at best bid.
      const sidePrice =
        instrument.side === 'sell' ? tickerData?.bestBidPrice : tickerData?.bestAskPrice
      const resolvedPrice = pickReferencePrice(
        sidePrice,
        tickerData?.markPrice,
        tickerData?.lastPrice
      )
      if (resolvedPrice) {
        // The tick at this price, not the base tickSize: venues with a per-price-band tick
        // (Upbit KRW, Hyperliquid) publish the finest band's tick as the base, which
        // prefilled BTC/KRW at ~115M with 7 decimals.
        const fullInstrument = multiExchangeInstrumentMapRef.current
          .get(parseExchangeField(instrument.exchange, primaryExchangeRef.current))
          ?.get(instrument.symbol)
        const pricePrecision =
          getEffectivePricePrecision(fullInstrument ?? instrument, Number(resolvedPrice)) ??
          getPrecisionFromStep(instrument.tickSize ?? instrument.pricePrecision, 2)
        form.setValue('price', formatNumberWithPrecision(resolvedPrice, pricePrecision), {
          shouldValidate: true,
        })
        hasSyncedRef.current = true
      }
    }
  }, [form])

  const handleNumberInput = useCallback(
    (field: 'price' | 'twapIntervalMinutes', value: string) => {
      if (value !== '' && !isValidNumberInput(value)) {
        return
      }
      userEditedRef.current = true
      markOrderFormManual(cardIdRef.current)
      const formatted = formatNumberWithCommas(value)
      form.setValue(field, formatted, {
        shouldDirty: true,
      })
      if (field === 'price') {
        hasSyncedRef.current = true
      }
    },
    [form]
  )

  // Handle amount change for a specific leg
  const handleAmountChange = useCallback(
    (index: number, amount: string) => {
      if (amount !== '' && !isValidNumberInput(amount)) {
        return
      }
      userEditedRef.current = true
      markOrderFormManual(cardIdRef.current)
      const formatted = formatNumberWithCommas(amount)
      form.setValue(`items.${index}.amount`, formatted, {
        shouldDirty: true,
      })
    },
    [form]
  )

  // Same as handleAmountChange, but applied to every member of a merged
  // multi-account visual row (see row-group-key.ts) — one input, N
  // items[i].amount writes, since the confirmed policy is one shared amount
  // across every account in a split row.
  const handleAmountChangeForGroup = useCallback(
    (indexes: number[], amount: string) => {
      if (amount !== '' && !isValidNumberInput(amount)) {
        return
      }
      userEditedRef.current = true
      markOrderFormManual(cardIdRef.current)
      const formatted = formatNumberWithCommas(amount)
      indexes.forEach((index) => {
        // A locked close row keeps the position's size, whoever writes (typing, orderbook click).
        if (form.getValues(`items.${index}.lockedAmount`)) return
        form.setValue(`items.${index}.amount`, formatted, {
          shouldDirty: true,
        })
      })
    },
    [form]
  )

  // Handle side change for a specific leg
  const handleSideChange = useCallback(
    (index: number, newSide: 'buy' | 'sell') => {
      const previousItem = form.getValues(`items.${index}`)
      if (!previousItem || previousItem.side === newSide) return
      userEditedRef.current = true
      markOrderFormManual(cardIdRef.current)
      form.setValue(`items.${index}.side`, newSide, {
        shouldValidate: true,
        shouldDirty: true,
      })
      // A different side is no longer the close of that position.
      // The row reads this live RHF flag rather than useFieldArray's metadata snapshot.
      form.setValue(`items.${index}.lockedAmount`, undefined)

      const targetItem = form.getValues(`items.${index}`)
      if (!targetItem) return

      // Direct match by (symbol, accountId) — NOT occurrence-index counting.
      // Occurrence counting assumed `items` and selectedInstruments/
      // selectedOptions stay in the same relative order for same-symbol
      // entries, which isn't reliable once accountId disambiguates them (a
      // split multi-account row, see row-group-key.ts); it could flip the
      // wrong leg's side. Every item is guaranteed an accountId by the
      // live-sync effect above, so this is a reliable key.
      if (targetItem.itemType === 'option') {
        setSelectedOptions((prev) =>
          prev.map((opt) =>
            opt.instrumentName === targetItem.symbol &&
            (opt.accountId ?? '') === (targetItem.accountId ?? '')
              ? lockAfterEdit(opt, {
                  ...opt,
                  tradeSide: newSide,
                  side: newSide === 'buy' ? 'ask' : 'bid',
                })
              : opt
          )
        )
      } else {
        setSelectedInstruments((prev) =>
          prev.map((inst) =>
            inst.symbol === targetItem.symbol &&
            (inst.accountId ?? '') === (targetItem.accountId ?? '')
              ? lockAfterEdit(inst, { ...inst, side: newSide })
              : inst
          )
        )
      }
    },
    [form, setSelectedOptions, setSelectedInstruments]
  )

  // Toggle side for single-leg mode
  const toggleSide = useCallback(
    (side: 'buy' | 'sell') => {
      handleSideChange(0, side)
    },
    [handleSideChange]
  )

  // Spreader mode only: toggle a leg's Quoting/Taking role. Exactly one leg must be QUOTING
  // and the other TAKING at all times (proto guide §3.2) — a 2-leg spreader ticket has no
  // valid "both taking" or "both quoting" state, so setting a leg to either role must flip
  // the OTHER leg to the opposite role in the same write, not just demote on the quoting
  // path. Missing the `role === 'taking'` mirror here previously left both legs `taking`
  // after swapping the quoting leg, which broke the `hasSpreaderLegs` invariant and kicked
  // the ticket back to the Limit tab (see the reset effect above).
  const handleSpreaderRoleChange = useCallback(
    (index: number, role: 'quoting' | 'taking') => {
      userEditedRef.current = true
      markOrderFormManual(cardIdRef.current)
      const otherRole = role === 'quoting' ? 'taking' : 'quoting'
      form.setValue(`items.${index}.spreaderRole`, role, { shouldDirty: true })
      const targetItem = form.getValues(`items.${index}`)
      if (!targetItem) return

      form.getValues('items').forEach((item, i) => {
        if (i !== index && item.spreaderRole === role) {
          form.setValue(`items.${i}.spreaderRole`, otherRole, { shouldDirty: true })
        }
      })

      // The "other" leg holding `role` could be an instrument or an option — only one of
      // these two setters will actually find it; the other is a no-op pass-through.
      const isTarget = (itemType: 'instrument' | 'option', symbol: string, accountId?: string) =>
        targetItem.itemType === itemType &&
        symbol === targetItem.symbol &&
        (accountId ?? '') === (targetItem.accountId ?? '')

      setSelectedInstruments((prev) =>
        prev.map((inst) => {
          if (isTarget('instrument', inst.symbol, inst.accountId)) {
            return { ...inst, spreaderRole: role }
          }
          if (inst.spreaderRole === role) return { ...inst, spreaderRole: otherRole }
          return inst
        })
      )
      setSelectedOptions((prev) =>
        prev.map((opt) => {
          if (isTarget('option', opt.instrumentName, opt.accountId)) {
            return { ...opt, spreaderRole: role }
          }
          if (opt.spreaderRole === role) return { ...opt, spreaderRole: otherRole }
          return opt
        })
      )
    },
    [form, setSelectedInstruments, setSelectedOptions]
  )

  // Turns the Pro dropdown's "Spreader" pick into an actual spreader ticket: if the legs
  // aren't tagged yet (picked via `canActivateSpreader`, not the spread-matrix widget),
  // default the first leg to quoting and the second to taking, then un-dismiss.
  //
  // Must write the role into BOTH `selectedInstruments` (drives `hasSpreaderLegs`, so this
  // is enough to flip into spreader mode) AND the react-hook-form `items[].spreaderRole`
  // field (what the QUOTING/TAKING badge in order-row.tsx actually reads via
  // `form.watch(...)`, defaulting to 'taking' when unset). Missing the form write left both
  // badges reading the 'taking' fallback even though the ticket itself was correctly
  // one-quoting/one-taking internally.
  const activateSpreader = useCallback(() => {
    setSpreaderSelected(true)
    if (!hasSpreaderLegs) {
      // Role assignment follows the form's OWN row order (not selectedInstruments/
      // selectedOptions array order, which can differ — the form preserves existing row
      // order across re-renders, see preserveCurrentItemOrder/isSameOrderItem above).
      const items = form.getValues('items')
      const roleByKey = new Map<string, 'quoting' | 'taking'>()
      items.forEach((item, i) => {
        const role = i === 0 ? 'quoting' : i === 1 ? 'taking' : undefined
        if (!role) return
        roleByKey.set(legKey(item.itemType, item.symbol, item.accountId), role)
        form.setValue(`items.${i}.spreaderRole`, role, { shouldDirty: true })
      })
      setSelectedInstruments((prev) =>
        prev.map((inst) => {
          const role = roleByKey.get(legKey('instrument', inst.symbol, inst.accountId))
          return role ? { ...inst, spreaderRole: role } : inst
        })
      )
      setSelectedOptions((prev) =>
        prev.map((opt) => {
          const role = roleByKey.get(legKey('option', opt.instrumentName, opt.accountId))
          return role ? { ...opt, spreaderRole: role } : opt
        })
      )
    }
    setSpreaderDismissed(false)
  }, [
    form,
    hasSpreaderLegs,
    setSelectedInstruments,
    setSelectedOptions,
    setSpreaderDismissed,
    setSpreaderSelected,
  ])

  // Appends a leg to an active spreader ticket via the "+ Add leg" picker. An empty
  // ticket starts with a quoting leg; every subsequent leg joins the taking side.
  // The user can promote another leg to quoting via its row's role badge, which
  // demotes whichever leg was quoting before.
  // Unlike `addAskSpread`/`addBidSpread` in the matrix widget, this APPENDS rather than
  // replacing the current selection — the whole point is building past 2 legs.
  const addSpreaderLeg = useCallback(
    (matrixInst: SpreaderMatrixInstrument) => {
      const role = selectedInstruments.length + selectedOptions.length === 0 ? 'quoting' : 'taking'
      const newInst = buildSpreaderSelectedInstrument(
        matrixInst,
        'buy',
        role,
        multiExchangeInstrumentMap
      )
      setSelectedInstruments((prev) => [...prev, newInst])
    },
    [
      multiExchangeInstrumentMap,
      selectedInstruments.length,
      selectedOptions.length,
      setSelectedInstruments,
    ]
  )

  // Get raw numeric values for API submission
  const getRawValues = useCallback((): RawOrderFormValues => {
    const values = form.getValues()
    return {
      items: values.items.map((item) => ({
        ...item,
        amount: item.amount ? parseFormattedNumber(item.amount) : 0,
      })),
      price: parseFormattedNumber(values.price || '0'),
      twapIntervalMinutes: parseFormattedNumber(values.twapIntervalMinutes || '0'),
      twapFrequencySeconds: parseFormattedNumber(values.twapFrequencySeconds || '0'),
      twapFrequencyMinSeconds: parseFormattedNumber(values.twapFrequencyMinSeconds || '0'),
      twapFrequencyMaxSeconds: parseFormattedNumber(values.twapFrequencyMaxSeconds || '0'),
      twapFrequencyMode: values.twapFrequencyMode || 'fixed',
      twapOrderSize: parseFormattedNumber(values.twapOrderSize || '0'),
      twapOrderSizeMin: parseFormattedNumber(values.twapOrderSizeMin || '0'),
      twapOrderSizeMax: parseFormattedNumber(values.twapOrderSizeMax || '0'),
      twapOrderSizeMode: values.twapOrderSizeMode || 'fixed',
      advancedTwapDurationSeconds: parseFormattedNumber(values.advancedTwapDurationSeconds || '0'),
      advancedTwapTotalSlices: values.advancedTwapTotalSlices || '',
      advancedTwapBandEnabled: values.advancedTwapBandEnabled ?? false,
      advancedTwapBandPct: parseFormattedNumber(values.advancedTwapBandPct || '0'),
      advancedTwapReduceOnly: values.advancedTwapReduceOnly ?? false,
    }
  }, [form])

  const visibleItemIndexes = useMemo(() => {
    return fields
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => {
        const exchange = parseExchangeField(field.exchange, primaryExchange)
        return orderAccounts.some((account) => accountExchange(account) === exchange)
      })
      .map(({ index }) => index)
  }, [accountExchange, fields, orderAccounts, primaryExchange])

  const getSubmissionRows = useCallback(() => {
    const values = form.getValues('items')
    const rawItems = getRawValues().items

    return visibleItemIndexes.flatMap((index) => {
      const item = values[index]
      const rawItem = rawItems[index]
      if (!item || !rawItem) return []
      const exchange = parseExchangeField(item.exchange, primaryExchange)
      const exchangeAccounts = orderAccounts.filter(
        (account) => accountExchange(account) === exchange
      )
      // Each row owns its account. This permits two rows on the same venue
      // (e.g. Deribit Personal and Deribit Spam) without one account selector
      // retargeting the other row before submission.
      const accountFilter = resolveItemAccountId(item, exchange)
      // `exchangeAccounts` is already order-eligible (ACTIVE, not read-only — see
      // `orderAccounts`), so matching against it is sufficient: an
      // existing-but-inactive/read-only account never appears here, dropping this
      // item from the batch the same way a never-existed one does, so
      // findUnresolvedSubmissionIndexes (called before every submit path below) can
      // catch it and block the whole submission instead of silently going out with
      // one leg missing.
      const matchingAccounts = exchangeAccounts.filter(
        (account) => account.accountId === accountFilter
      )
      return matchingAccounts.map((account) => ({ item, rawItem, index, exchange, account }))
    })
  }, [
    accountExchange,
    form,
    getRawValues,
    orderAccounts,
    primaryExchange,
    resolveItemAccountId,
    visibleItemIndexes,
  ])

  // Must be called (and checked) before EVERY submit path below — a row whose
  // accountId doesn't resolve to a valid, active, correct-exchange account
  // simply produces no entry in getSubmissionRows()'s output (see its own
  // comment), which would otherwise submit a silently-partial batch. For a
  // multi-account split row (e.g. one leg long, one short), submitting only
  // the legs that happened to resolve is an accidental partial hedge, not a
  // benign partial failure — so this blocks the ENTIRE submission instead.
  const validateSubmissionRows = useCallback((): boolean => {
    const resolvedIndexes = getSubmissionRows().map((row) => row.index)
    const unresolved = findUnresolvedSubmissionIndexes(visibleItemIndexes, resolvedIndexes)
    if (unresolved.length === 0) return true
    const items = form.getValues('items')
    const symbols = unresolved.map((index) => items[index]?.symbol).filter(Boolean)
    toast.error(
      symbols.length > 0
        ? t('invalidAccountForRows', { symbols: symbols.map(formatSymbol).join(', ') })
        : t('invalidAccountForOrder'),
      { position: 'bottom-right' }
    )
    return false
  }, [form, getSubmissionRows, t, visibleItemIndexes])

  // The Hedge tab hedges the first row's account, and with no rows at all the account the
  // ticket would use on its own venue. A row whose account doesn't resolve blocks it instead.
  const getSingleHedgeTarget = useCallback((): SingleHedgeTarget | null => {
    const row = getSubmissionRows()[0]
    if (row) {
      return { account: row.account, exchange: row.exchange, selectedSymbol: row.item.symbol }
    }
    if (visibleItemIndexes.length > 0) return null
    const accountId = resolveInheritedAccountId(primaryExchange)
    const account = orderAccounts.find((candidate) => candidate.accountId === accountId)
    return account ? { account, exchange: primaryExchange, selectedSymbol: '' } : null
  }, [
    getSubmissionRows,
    visibleItemIndexes,
    resolveInheritedAccountId,
    primaryExchange,
    orderAccounts,
  ])

  const singleHedgeTabAtom = useMemo(() => singleHedgeTabAtomFamily(cardId), [cardId])
  // Positions are read at submit time, not subscribed to: they tick with every mark price.
  const buildSingleHedge = useCallback((): SingleHedgeSubmission | null => {
    const target = getSingleHedgeTarget()
    if (!target) {
      toast.error(t('selectAccountFirst'), { position: 'bottom-right' })
      return null
    }
    const result = buildSingleHedgeTabParams(
      store.get(singleHedgeTabAtom),
      singleHedgeContext({
        selectedSymbol: target.selectedSymbol,
        accountId: target.account.accountId,
        exchange: target.exchange,
        allPositions: store.get(allPositionsAtom),
        instrumentMap,
        multiExchangeInstrumentMap,
      }),
      target.account.accountId
    )
    if (!result.ok) {
      toast.error(result.errors[0].message, { position: 'bottom-right' })
      return null
    }
    return { ...target, params: result.params }
  }, [
    getSingleHedgeTarget,
    t,
    store,
    singleHedgeTabAtom,
    instrumentMap,
    multiExchangeInstrumentMap,
  ])

  const bumpExecutedCount = useSetAtom(orderFormExecutedCountAtomFamily(cardId))
  const markOrderExecuted = useCallback(() => bumpExecutedCount((n) => n + 1), [bumpExecutedCount])

  // Reset form after successful order
  const resetFormAfterOrder = useCallback(() => {
    markOrderExecuted()
    clearSubmitTimeout()
    // The persisted localStorage blob (use-order-form-values-persist.ts) only exists so a
    // failed submission's values survive an accidental reload — once the order actually
    // goes through there's nothing left worth recovering, so drop it immediately rather
    // than waiting on that hook's own debounced re-save of the now-reset form.
    localStorage.removeItem(orderFormValuesStorageKey(cardId))
    const currentItems = form.getValues('items')

    form.reset({
      items: currentItems.map((item) => ({ ...item, amount: '', lockedAmount: undefined })),
      price: '',
      priceAnchor: 'price',
      priceOffsetUnit: 'quote_value',
      twapIntervalMinutes: '',
      twapFrequencySeconds: '1',
      twapFrequencyMinSeconds: '',
      twapFrequencyMaxSeconds: '',
      twapFrequencyMode: 'fixed',
      twapOrderSize: '',
      twapOrderSizeMin: '',
      twapOrderSizeMax: '',
      twapOrderSizeMode: 'fixed',
      spreaderSlicePct: '',
      spreaderLimitPrice: '',
      advancedTwapDurationSeconds: '',
      advancedTwapTotalSlices: '',
      advancedTwapBandEnabled: false,
      advancedTwapBandPct: '',
      advancedTwapReduceOnly: false,
      ...SCALE_FORM_DEFAULTS,
    })
    // The selection atoms carry the lock too; left set, the form sync would re-lock the blanked row.
    setSelectedOptions((prev) =>
      prev.map((o) => (o.lockedAmount ? { ...o, lockedAmount: undefined } : o))
    )
    setSelectedInstruments((prev) =>
      prev.map((i) =>
        i.lockedAmount ? { ...i, lockedAmount: undefined, lockedAmountUnit: undefined } : i
      )
    )
    // Trigger 2: 주문 제출 후 리셋 — 현재 마켓 가격으로 재동기화
    hasSyncedRef.current = false
    userEditedRef.current = false
    syncPrice()
    setIsSubmitting(false)
  }, [
    form,
    clearSubmitTimeout,
    syncPrice,
    cardId,
    markOrderExecuted,
    setSelectedOptions,
    setSelectedInstruments,
  ])

  // Everything the submitters in lib/submitters share. Selection and accounts are
  // read through refs at submit time so their identities don't churn the callbacks.
  const getSelection = useCallback(
    () => ({
      instruments: selectedInstrumentsRef.current,
      options: selectedOptionsRef.current,
    }),
    []
  )
  const getAnalyticsAccounts = useCallback(() => analyticsAccountsRef.current, [])
  const submitterContext = useMemo<SubmitterContext>(
    () => ({
      t,
      form,
      validateSubmissionRows,
      getSubmissionRows,
      getRawValues,
      setIsSubmitting,
      resetFormAfterOrder,
      markOrderExecuted,
      triggerOptimistic,
      getSelection,
      getAnalyticsAccounts,
    }),
    [
      t,
      form,
      validateSubmissionRows,
      getSubmissionRows,
      getRawValues,
      resetFormAfterOrder,
      markOrderExecuted,
      triggerOptimistic,
      getSelection,
      getAnalyticsAccounts,
    ]
  )

  const submitTwapOrder = useCallback(
    () => submitTwap(submitterContext, { instruments, instrumentMap, startTwap, startTwapBatch }),
    [submitterContext, instruments, instrumentMap, startTwap, startTwapBatch]
  )

  const submitAdvancedTwapOrder = useCallback(
    () => submitAdvancedTwap(submitterContext, { instrumentMap, startAdvancedTwap }),
    [submitterContext, instrumentMap, startAdvancedTwap]
  )

  const submitScaleOrder = useCallback(
    () => submitScale(submitterContext, { instrumentMap, startScale }),
    [submitterContext, instrumentMap, startScale]
  )

  const submitSpreaderOrder = useCallback(
    () => submitSpreader(submitterContext, { multiExchangeInstrumentMap, startSpreader }),
    [submitterContext, multiExchangeInstrumentMap, startSpreader]
  )

  const submitSingleHedgeOrder = useCallback(
    () => submitSingleHedge(submitterContext, { buildSingleHedge, startSingleHedge }),
    [submitterContext, buildSingleHedge, startSingleHedge]
  )

  const submitLimitChaseOrder = useCallback(
    () =>
      submitLimitChase(submitterContext, {
        instruments,
        instrumentMap,
        startLimitChase,
        startLimitChaseBatch,
      }),
    [submitterContext, instruments, instrumentMap, startLimitChase, startLimitChaseBatch]
  )

  const submitLimitMarketOrder = useCallback(
    () =>
      submitLimitMarket(submitterContext, {
        instruments,
        instrumentMap,
        isMarketOrder,
        supportsTriggerOrders,
        getCollapsibleConfig,
        createOrderBatch,
      }),
    [
      submitterContext,
      instruments,
      instrumentMap,
      isMarketOrder,
      supportsTriggerOrders,
      getCollapsibleConfig,
      createOrderBatch,
    ]
  )

  const submitTriggerAlgoOrder = useCallback(
    () =>
      submitTriggerAlgo(submitterContext, {
        instruments,
        isMarketOrder,
        isTwapMode,
        isLimitChaseMode,
        supportsTriggerOrders,
        getCollapsibleConfig,
        confirmPlan,
        buildSingleHedge: isSingleHedgeMode ? buildSingleHedge : undefined,
      }),
    [
      submitterContext,
      instruments,
      isMarketOrder,
      isTwapMode,
      isLimitChaseMode,
      supportsTriggerOrders,
      getCollapsibleConfig,
      confirmPlan,
      isSingleHedgeMode,
      buildSingleHedge,
    ]
  )

  // Unified submit order — a configured entry condition arms the trigger only
  // when it both holds data AND is enabled. The enable flag lets the user keep
  // their inputs but submit a normal order by flipping the trigger off.
  // The trigger condition is evaluated and fired by OrderX itself, so it works on every
  // venue; only the venue-native TP/SL exit bracket depends on supportsTriggerOrders.
  const hasTriggerCondition = collapsibleStatus.hasTrigger
  const hasExitCondition = collapsibleStatus.hasExit && supportsTriggerOrders

  // Why the star/"save as preset" action is unusable right now, if at all.
  // Presets replay via buildCurrentPresetPayload alone (see there) — anything
  // not handled by one of its branches would either be rejected there or,
  // worse, silently mis-saved as a plain manual order. Surface the reason here
  // so SavePresetButton can disable itself instead of letting that happen.
  // Exit Condition (TP/SL) is NOT in this list — buildManualPresetPayload
  // attaches it as extra reduce-only legs, same as the live submit path.
  const presetUnsupportedReason:
    | 'advancedTwap'
    | 'scale'
    | 'singleHedge'
    | 'spreader'
    | 'trigger'
    | null = isAdvancedTwapMode
    ? 'advancedTwap'
    : isScaleMode
      ? 'scale'
      : isSingleHedgeMode
        ? 'singleHedge'
        : isSpreaderMode
          ? 'spreader'
          : hasTriggerCondition
            ? 'trigger'
            : null

  // Every row carries its own side, so the clicked button's side only picks nothing
  // here; it stays in the signature for the buy/sell callers.
  const submitOrder = useCallback(
    (_side: 'buy' | 'sell') => {
      // Every mode goes through here: tell the user up front when a row's venue is down.
      warnIfVenueDown(
        getSubmissionRows().map(({ item, account }) => ({
          accountId: account.accountId,
          symbol: item.symbol,
        }))
      )
      if (isSpreaderMode) return submitSpreaderOrder()
      if (isScaleMode) {
        // Checked before the trigger: an armed trigger would otherwise fire a plain
        // order, and the scale path has no way to carry it. (The exit bracket is
        // disabled form-wide by withoutExitBracket, so it needs no check here.)
        if (hasTriggerCondition) {
          toast.error(t('scaleForm.conditionsUnsupported'), { position: 'bottom-right' })
          return
        }
        return submitScaleOrder()
      }
      if (hasTriggerCondition) return submitTriggerAlgoOrder()
      if (isSingleHedgeMode) return submitSingleHedgeOrder()
      if (isTwapMode) return submitTwapOrder()
      if (isAdvancedTwapMode) return submitAdvancedTwapOrder()
      if (isLimitChaseMode) return submitLimitChaseOrder()
      return submitLimitMarketOrder()
    },
    [
      warnIfVenueDown,
      getSubmissionRows,
      isSpreaderMode,
      submitSpreaderOrder,
      hasTriggerCondition,
      isScaleMode,
      submitScaleOrder,
      t,
      isSingleHedgeMode,
      submitSingleHedgeOrder,
      isTwapMode,
      isAdvancedTwapMode,
      isLimitChaseMode,
      submitTriggerAlgoOrder,
      submitTwapOrder,
      submitAdvancedTwapOrder,
      submitLimitChaseOrder,
      submitLimitMarketOrder,
    ]
  )

  // Build a PresetPayload from the current form state (without submitting)
  const buildCurrentPresetPayload = useCallback((): PresetPayload | null => {
    // Mirrors presetUnsupportedReason: Advanced TWAP and an armed entry
    // trigger have no representation in PresetPayload, and Spreader would
    // silently fall through to buildManualPresetPayload and save the wrong
    // thing. (Exit Condition is NOT gated here — see below, it's attached as
    // extra legs.) SavePresetButton disables itself using the same reason, so
    // this is a second, authoritative guard against callers that skip that check.
    if (presetUnsupportedReason) return null

    const rawValues = getRawValues()
    const collapsibleConfig = getCollapsibleConfig()
    // Same rows the live submit sends, each bound to its own account. A visible row
    // with no valid account would otherwise be dropped from the preset silently.
    const rows = getSubmissionRows()
    if (rows.length === 0) return null
    const resolvedIndexes = rows.map((row) => row.index)
    if (findUnresolvedSubmissionIndexes(visibleItemIndexes, resolvedIndexes).length > 0) {
      return null
    }

    const formItems: FormItemRaw[] = rows.map(({ item, rawItem, exchange, account }) => ({
      symbol: item.symbol,
      side: item.side as 'buy' | 'sell',
      amount: rawItem.amount ?? 0,
      amountUnit: item.amountUnit,
      baseCurrency: item.baseCurrency,
      itemType: item.itemType as 'instrument' | 'option',
      contractSize: item.contractSize,
      accountId: account.accountId,
      exchange,
      tradeVenue: account.tradeVenue,
    }))

    if (isTwapMode) {
      return buildTwapPresetPayload({
        items: formItems,
        twapIntervalMinutes: rawValues.twapIntervalMinutes,
        twapFrequencySeconds: rawValues.twapFrequencySeconds,
        twapFrequencyMinSeconds: rawValues.twapFrequencyMinSeconds,
        twapFrequencyMaxSeconds: rawValues.twapFrequencyMaxSeconds,
        twapFrequencyMode: rawValues.twapFrequencyMode,
        twapOrderSize: rawValues.twapOrderSize,
        twapOrderSizeMin: rawValues.twapOrderSizeMin,
        twapOrderSizeMax: rawValues.twapOrderSizeMax,
        twapOrderSizeMode: rawValues.twapOrderSizeMode,
      })
    }

    if (isLimitChaseMode) {
      return buildLimitChasePresetPayload({ items: formItems }, instrumentMap)
    }

    return buildManualPresetPayload(
      {
        items: formItems,
        price: form.getValues('price') ?? '',
        priceAnchor: form.getValues('priceAnchor') ?? 'price',
        priceOffsetUnit: form.getValues('priceOffsetUnit') ?? 'quote_value',
        isMarketOrder,
        timeInForce: form.getValues('timeInForce'),
        reduceOnly: form.getValues('reduceOnly') ?? false,
        postOnly: form.getValues('postOnly') ?? false,
        exitCondition: hasExitCondition
          ? { takeProfit: collapsibleConfig?.priceAbove, stopLoss: collapsibleConfig?.priceBelow }
          : undefined,
      },
      instrumentMap
    )
  }, [
    presetUnsupportedReason,
    hasExitCondition,
    getCollapsibleConfig,
    form,
    getRawValues,
    getSubmissionRows,
    visibleItemIndexes,
    isTwapMode,
    isLimitChaseMode,
    isMarketOrder,
    instrumentMap,
  ])

  // syncPrice is stable callback → effects below run only when dependencies change.
  // Never re-runs from live ticker updates.

  // Trigger 1: Component mount (1x) — sync price when price field first renders
  useEffect(() => {
    syncPrice()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Trigger 5: returning to the Limit tab from Market/TWAP/LimitChase/RFQ.
  // hasSyncedRef only guarantees the price field gets filled ONCE — after that,
  // switching order-type tabs and coming back left whatever price was synced at
  // mount frozen in place, even if the market has since moved, because nothing
  // reset hasSyncedRef on a tab change. Only auto-fills (syncPrice never sets
  // shouldDirty) skip the resync if the user actually typed a price themselves.
  useEffect(() => {
    if (selectedTab !== OrderFormSummaryTab.Limit) return
    if (form.getFieldState('price').isDirty) return
    hasSyncedRef.current = false
    syncPrice()
  }, [selectedTab, syncPrice, form])

  // Trigger 4: selected ticker change → form reset + sync new ticker price
  useEffect(() => {
    if (selectedSymbol?.id && prevSymbolIdRef.current !== selectedSymbol.id) {
      form.reset({
        items: form.getValues('items'),
        price: '',
        priceAnchor: 'price',
        priceOffsetUnit: 'quote_value',
        twapIntervalMinutes: '',
        twapFrequencySeconds: '1',
        twapFrequencyMinSeconds: '',
        twapFrequencyMaxSeconds: '',
        twapFrequencyMode: 'fixed',
        twapOrderSize: '10',
        twapOrderSizeMin: '',
        twapOrderSizeMax: '',
        twapOrderSizeMode: 'fixed',
        advancedTwapDurationSeconds: '',
        advancedTwapTotalSlices: '',
        advancedTwapBandEnabled: false,
        advancedTwapBandPct: '',
        advancedTwapReduceOnly: false,
        ...SCALE_FORM_DEFAULTS,
      })
      hasSyncedRef.current = false
      userEditedRef.current = false
      prevSymbolIdRef.current = selectedSymbol.id
      syncPrice()
    }
  }, [selectedSymbol?.id, form, syncPrice])

  // Trigger 3: multi-leg → single-leg (incl. items 0→1)
  useEffect(() => {
    if (isSingleVisualRow) {
      hasSyncedRef.current = false
      syncPrice()
    }
  }, [isSingleVisualRow, syncPrice])

  // Read through a ref by the row sync below (a preference change must not rebuild the rows);
  // the effect after it applies a change to the rows already there.
  const denomPreference = useAtomValue(orderFormDenomPreferenceAtomFamily(cardId))
  const denomPreferenceRef = useRef(denomPreference)
  denomPreferenceRef.current = denomPreference
  const appliedDenomPreferenceRef = useRef(denomPreference)
  useEffect(() => {
    if (appliedDenomPreferenceRef.current === denomPreference) return
    appliedDenomPreferenceRef.current = denomPreference
    form.getValues('items').forEach((item, index) => {
      // A locked close row keeps the position's size and unit.
      if (item.itemType === 'option' || item.lockedAmount) return
      const inst = selectedInstrumentsRef.current.find(
        (i) => i.symbol === item.symbol && (i.accountId ?? '') === (item.accountId ?? '')
      )
      const unit = defaultInstrumentAmountUnit(
        inst ?? ({ ...item, side: item.side } as SelectedInstrument),
        denomPreference
      )
      if (unit === item.amountUnit) return
      form.setValue(`items.${index}.amountUnit`, unit, { shouldDirty: true })
      // The number was typed in the old unit; carrying it over would silently resize the order.
      if (item.amount) form.setValue(`items.${index}.amount`, '', { shouldDirty: true })
    })
  }, [denomPreference, form])

  // Each row's amount as the jotai selection last held it, to tell a real atom-side change
  // (a position-table add, a preset) from an atom that simply hasn't caught up with typing.
  const lastAtomAmountsRef = useRef(new Map<string, string | undefined>())

  // Sync items from atoms - combine both options and instruments
  // Preserve existing amounts when updating items (e.g., when side changes)
  useEffect(() => {
    const currentItems = form.getValues('items')
    const optionItems = optionsToItems(selectedOptions)
    const instrumentItems = instrumentsToItems(selectedInstruments, denomPreferenceRef.current)
    const newItems = preserveCurrentItemOrder(currentItems, [...optionItems, ...instrumentItems])

    const atomAmountKey = (item: { itemType?: string; symbol: string; accountId?: string }) =>
      `${item.itemType ?? ''}:${item.symbol}:${item.accountId ?? ''}`
    const prevAtomAmounts = lastAtomAmountsRef.current
    lastAtomAmountsRef.current = new Map(newItems.map((item) => [atomAmountKey(item), item.amount]))

    const countChanged = newItems.length !== currentItems.length
    const contentChanged =
      !countChanged &&
      newItems.some((newItem, i) => {
        const cur = currentItems[i]
        return (
          !cur ||
          cur.symbol !== newItem.symbol ||
          cur.itemType !== newItem.itemType ||
          cur.side !== newItem.side
        )
      })

    if (countChanged) {
      const currentPrefixMatches =
        newItems.length > currentItems.length &&
        currentItems.every((cur, i) => {
          const next = newItems[i]
          return next && isSameOrderItem(cur, next) && cur.side === next.side
        })

      if (currentPrefixMatches) {
        append(newItems.slice(currentItems.length))
        return
      }

      const itemsWithPreserved = newItems.map((newItem) => {
        const existingItem =
          currentItems.find((item) => isSameOrderItem(item, newItem)) ??
          currentItems.find((item) => isSameVisualOrderItem(item, newItem))
        if (existingItem) {
          return mergeRowAmount(newItem, existingItem)
        }
        return newItem
      })
      replace(itemsWithPreserved)
    } else if (contentChanged) {
      newItems.forEach((newItem, i) => {
        const cur = currentItems[i]
        if (
          cur &&
          (cur.symbol !== newItem.symbol ||
            cur.itemType !== newItem.itemType ||
            cur.side !== newItem.side)
        ) {
          update(i, mergeRowAmount(newItem, cur))
        }
      })
    } else {
      newItems.forEach((newItem, i) => {
        const cur = currentItems[i]
        // This branch handles in-place metadata changes for the same visual
        // row. Do not use isSameOrderItem here: accountId is part of that
        // identity, so it would reject the exact account-tab normalization
        // change this branch needs to copy into RHF.
        if (!cur || cur.symbol !== newItem.symbol || cur.itemType !== newItem.itemType) return
        // Copy only an amount that genuinely moved on the atom — see shouldCopyAtomAmount:
        // "atom ≠ form" alone swapped the two every render (0.03656 ETH ↔ 100 USD).
        const amountChanged = newItem.lockedAmount
          ? newItem.amount !== cur.amount
          : shouldCopyAtomAmount({
              atomAmount: newItem.amount,
              previousAtomAmount: prevAtomAmounts.get(atomAmountKey(newItem)),
              formEchoes: formEchoAmountsRef.current.get(`${newItem.itemType}:${newItem.symbol}`),
              formAmount: cur.amount,
            })
        // accountId isn't part of `contentChanged`'s identity check above
        // (symbol/itemType/side only), so an
        // in-place accountId edit on an EXISTING same-count item — e.g. the
        // live-sync effect filling in a missing one — falls through to this
        // branch and must be propagated explicitly, or the RHF field (what
        // order-form-summary.tsx's preview panel actually reads via
        // form.watch('items')) keeps the stale accountId the jotai atom no
        // longer has.
        const accountChanged = cur.accountId !== newItem.accountId
        // A re-close of the same symbol and side sets or clears the lock in place.
        const lockChanged = !!cur.lockedAmount !== !!newItem.lockedAmount
        const unitChanged = !!newItem.lockedAmount && cur.amountUnit !== newItem.amountUnit
        if (!amountChanged && !accountChanged && !lockChanged && !unitChanged) return
        // `useFieldArray.update()` unmounts and remounts the complete row. Avoid it for
        // amount-only changes, which can arrive on every keystroke through the atom/RHF
        // bridge. Account identity is structural metadata consumed from `fields`, so an
        // actual account change still needs one guarded update to refresh that snapshot.
        if (accountChanged || lockChanged || unitChanged) {
          update(i, {
            ...cur,
            amount: amountChanged ? newItem.amount : cur.amount,
            amountUnit: newItem.lockedAmount
              ? newItem.amountUnit
              : cur.amountUnit || newItem.amountUnit,
            accountId: newItem.accountId,
            lockedAmount: newItem.lockedAmount,
          })
        } else if (amountChanged) {
          form.setValue(`items.${i}.amount`, newItem.amount, { shouldDirty: false })
        }
      })
    }
  }, [selectedOptions, selectedInstruments, append, replace, update, form])

  // Always-on ticker subscription for the currently selected leg symbols — the
  // form used to rely entirely on OTHER widgets (chart, watchlist rows) having
  // already subscribed to watchListTickerMapAtom for this exact symbol. Entry
  // points that don't touch the chart's linking selection (position table,
  // open-orders table, order history, ticker tape) left the map with no entry
  // at all, so the price field stayed blank forever. Mirrors the pattern in
  // exit-condition.tsx, but always mounted instead of gated behind a
  // collapsible section.
  const priceSyncPayloads = useMemo(
    () => buildPriceSyncPayloads(selectedOptions, selectedInstruments, primaryExchange),
    [selectedOptions, selectedInstruments, primaryExchange]
  )

  useMultiMarketDataStream({
    connected: priceSyncPayloads.length > 0,
    payloads: priceSyncPayloads,
    onMessage: (data) => {
      if (!data?.tickers?.length) return
      for (const ticker of data.tickers) {
        const { symbol: sym, lastPrice, ...stats } = ticker
        const key = watchlistEntryKey(ticker.exchange, sym)
        scheduleTickerMapWrite(store, key, (current) => {
          const bestBidPrice = stats.bidPrice || current?.bestBidPrice
          const bestAskPrice = stats.askPrice || current?.bestAskPrice
          if (
            current?.lastPrice === lastPrice &&
            current?.markPrice === stats.markPrice &&
            current?.bestBidPrice === bestBidPrice &&
            current?.bestAskPrice === bestAskPrice
          ) {
            return current
          }
          return {
            ...current,
            lastPrice,
            markPrice: stats.markPrice || current?.markPrice,
            bestBidPrice,
            bestAskPrice,
            prevLastPrice: current?.lastPrice || lastPrice,
          }
        })
      }
    },
  })

  // Update tickerMapRef, retrying the price sync while unsynced — the initial
  // sync attempts (mount / symbol change / single-leg) can fire before this
  // symbol's ticker data has arrived, leaving the price field empty.
  // store.sub (not useAtomValue): keeps the ref fresh and retries the sync
  // without re-rendering the provider on every ticker tick.
  useEffect(() => {
    const applyLatest = () => {
      tickerMapRef.current = store.get(watchListTickerMapAtom)
      if (!hasSyncedRef.current) syncPrice()
    }
    applyLatest()
    return store.sub(watchListTickerMapAtom, applyLatest)
  }, [store, syncPrice])

  // Sync selectedTab to server config (debounced). The mount value is the restored one
  // (useOrderFormConfigSync already skips it too) — only a change is worth a write.
  const selectedTabSyncedRef = useRef<OrderFormSummaryTab | null>(null)
  useEffect(() => {
    const prevTab = selectedTabSyncedRef.current
    selectedTabSyncedRef.current = selectedTab
    const tabChanged = prevTab !== null && prevTab !== selectedTab
    const timeoutId = setTimeout(() => {
      if (tabChanged) syncConfig(cardId, { orderType: selectedTab })
      // The popup card has no layout widget for server restore to find — mirror locally
      if (popupCardId === cardId) {
        setPopupConfig((prev) => ({ ...prev, cardId, orderType: selectedTab }))
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [cardId, selectedTab, syncConfig, popupCardId, setPopupConfig])

  // Sync the rows themselves (not their sizes) to server config (debounced), so rows added
  // from the watchlist/chart survive a reload — restored by restoreOrderForm. Same
  // skip-the-mount-value rule as the tab above.
  const orderRowsSyncedRef = useRef<string | null>(null)
  useEffect(() => {
    const serialized = serializeOrderRows({ selectedOptions, selectedInstruments })
    const prevRows = orderRowsSyncedRef.current
    orderRowsSyncedRef.current = serialized
    if (prevRows === null || prevRows === serialized) return
    const timeoutId = setTimeout(() => {
      syncConfig(cardId, { [ORDER_ROWS_CONFIG_KEY]: serialized })
      // The popup card has no layout widget for server restore to find — mirror locally
      if (popupCardId === cardId) {
        setPopupConfig((prev) => ({ ...prev, cardId, [ORDER_ROWS_CONFIG_KEY]: serialized }))
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [cardId, selectedOptions, selectedInstruments, syncConfig, popupCardId, setPopupConfig])

  // Sync form values to server config (debounced).
  //
  // `form.watch()` with no arguments — the way this used to read form values — subscribes
  // the CALLING COMPONENT to re-render on every field change across every leg. Since this
  // hook backs OrderFormProvider's context value, that turned every keystroke (amount,
  // side, price, TWAP/entry/exit condition fields) into a full re-render of this hook plus
  // every one of its ~18 context consumers (summary panel, submit/buy-sell buttons,
  // collapsibles, strike/expiry dropdowns, TWAP/RFQ params, ...), even though the value is
  // only ever read inside this one already-debounced effect below. `form.watch(callback)`
  // (the subscription form, same pattern as the tickerMapRef sync above) observes changes
  // without triggering any render at all.
  const formSyncPrevRef = useRef<string | null>(null)
  const formSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const runFormSync = (values: DeepPartialSkipArrayKey<UnifiedOrderFormValues>) => {
      const { items: currentItems, ...rest } = values
      const itemAmounts: Record<string, string> = {}
      currentItems?.forEach((item) => {
        if (item?.symbol && item?.amount) itemAmounts[item.symbol] = item.amount
      })

      // Keys to exclude from automatic form-sync persistence
      const EXCLUDED_KEYS = new Set([
        // Managed independently by useOrderFormCollapsibleConfig
        'priceAbove',
        'priceBelow',
        'entryConditionInstrument',
        'entryConditionOperator',
        'entryConditionPrice',
        'entryConditionTriggerType',
        'entryConditionPriceRef',
        'entryConditionDelaySeconds',
        'entryConditionExpiryMinutes',
        'entryConditionScheduleVariant',
        'entryConditionFireAtMs',
        'entryConditionActionEventType',
        'entryConditionActionTargetType',
        'entryConditionActionTargetValue',
        'entryConditionActionPartialFillThreshold',
        'entryConditionLogic',
        'entryConditionGroupJson',
        'timeInForce',
        'expiresValue',
        'expiresUnit',
        'reduceOnly',
        'postOnly',

        // Anchor/offset — always reset to defaults on load
        'priceAnchor',
        'priceOffsetUnit',

        // TWAP settings — do not persist (user request)
        'twapIntervalMinutes',
        'twapFrequencySeconds',
        'twapFrequencyMinSeconds',
        'twapFrequencyMaxSeconds',
        'twapFrequencyMode',
        'twapOrderSize',
        'twapOrderSizeMin',
        'twapOrderSizeMax',
        'twapOrderSizeMode',
      ])

      const { price: _price, ...restWithoutPrice } = rest
      const config = Object.fromEntries(
        Object.entries(restWithoutPrice).filter(([k, v]) => v != null && !EXCLUDED_KEYS.has(k))
      ) as Record<string, string>

      if (Object.keys(itemAmounts).length > 0) {
        config.itemAmounts = JSON.stringify(itemAmounts)
      } else {
        config.itemAmounts = '' // Clear if no amounts
      }

      const key = JSON.stringify(config)
      if (formSyncPrevRef.current === null) {
        formSyncPrevRef.current = key
        return
      }
      if (formSyncPrevRef.current === key) return
      formSyncPrevRef.current = key

      if (formSyncTimeoutRef.current !== null) clearTimeout(formSyncTimeoutRef.current)
      formSyncTimeoutRef.current = setTimeout(() => {
        formSyncTimeoutRef.current = null
        syncConfig(cardId, config)
        // The popup card has no layout widget for server restore to find — mirror locally
        if (popupCardId === cardId) {
          setPopupConfig((prev) => ({ ...prev, cardId, ...config }))
        }
      }, 500)
    }

    runFormSync(form.getValues())
    const subscription = form.watch(runFormSync)
    return () => {
      subscription.unsubscribe()
      if (formSyncTimeoutRef.current !== null) clearTimeout(formSyncTimeoutRef.current)
    }
  }, [form, cardId, syncConfig, popupCardId, setPopupConfig])

  const markPriceSynced = useCallback(() => {
    hasSyncedRef.current = true
  }, [])
  const syncPriceFromAtm = useCallback(() => {
    hasSyncedRef.current = false
    syncPrice()
  }, [syncPrice])
  const hasUnsavedUserEdits = useCallback(() => userEditedRef.current, [])

  // Consumers read form values with render-time `form.watch(name)` (order-form-summary,
  // unified-order-form, tab-form-type, exit-condition, rfq-params). That only re-renders
  // this hook's owner, and they see the new value because the Provider value changes
  // with it — so the memo must change on every value change too. `form` itself is a
  // stable reference and can't signal that.
  const formValues = useWatch({ control: form.control })

  return useMemo(
    () => ({
      // Mode
      mode,
      isTwapMode,
      isAdvancedTwapMode,
      isScaleMode,
      isLimitChaseMode,
      isMarketOrder,
      isSingleHedgeMode,
      isSpreaderMode,
      // Whether the legs are still spreader-tagged even if `isSpreaderMode` is
      // currently false (dismissed) — lets a "Spreader" item stay selectable in
      // the Pro dropdown to switch back, see tab-form-type.tsx.
      hasSpreaderLegs,
      // Whether the current 2 legs are eligible to become a spreader ticket even
      // without spreaderRole tags yet — see tab-form-type.tsx.
      canActivateSpreader,
      activateSpreader,
      addSpreaderLeg,
      setSpreaderDismissed,
      supportsTriggerOrders,
      primaryExchange,
      // Visual-row identity — see row-group-key.ts. isSingleVisualRow drives
      // layout-only single/multi-leg UI (price anchor, denomination, etc.);
      // isSplitAccountRow/hasMixedSides are only meaningful when
      // isSingleVisualRow is true, and hasMixedSides is the ONE flag
      // side-dependent features (TP/SL, exit bracket) must gate on — a
      // same-side account split is still safe for those.
      isSingleVisualRow,
      isSplitAccountRow,
      hasMixedSides,

      // Form
      form,
      fields,
      orderAccounts,
      visibleItemIndexes,
      setOrderAccountFilter,
      resolveInheritedAccountId,
      resolveItemAccountId,
      getSubmissionRows,
      validateSubmissionRows,
      getSingleHedgeTarget,
      // Exposed so callers outside this hook (e.g. tab-form-type.tsx exiting
      // spreader mode by stripping `spreaderRole` off the selected legs) can
      // write this card's own selection without going through
      // useOrderFormDispatch, which resolves whichever card is currently
      // focused rather than necessarily this one.
      setSelectedInstruments,

      // Handlers
      handleNumberInput,
      handleAmountChange,
      handleAmountChangeForGroup,
      handleSideChange,
      toggleSide,
      handleSpreaderRoleChange,
      submitOrder,
      buildCurrentPresetPayload,
      presetUnsupportedReason,
      hasTriggerCondition,
      hasExitCondition,
      markPriceSynced,
      syncPriceFromAtm,

      // Values
      getRawValues,

      // State
      isSubmitting,
      // See userEditedRef's comment above — deliberately not derived from
      // form.formState.isDirty/dirtyFields, which the restore effect also sets.
      hasUnsavedUserEdits,
    }),
    // formValues isn't read inside; it's the value-change signal described above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mode,
      isTwapMode,
      isAdvancedTwapMode,
      isScaleMode,
      isLimitChaseMode,
      isMarketOrder,
      isSingleHedgeMode,
      isSpreaderMode,
      hasSpreaderLegs,
      canActivateSpreader,
      activateSpreader,
      addSpreaderLeg,
      setSpreaderDismissed,
      supportsTriggerOrders,
      primaryExchange,
      isSingleVisualRow,
      isSplitAccountRow,
      hasMixedSides,
      form,
      formValues,
      fields,
      orderAccounts,
      visibleItemIndexes,
      setOrderAccountFilter,
      resolveInheritedAccountId,
      resolveItemAccountId,
      getSubmissionRows,
      validateSubmissionRows,
      getSingleHedgeTarget,
      setSelectedInstruments,
      handleNumberInput,
      handleAmountChange,
      handleAmountChangeForGroup,
      handleSideChange,
      toggleSide,
      handleSpreaderRoleChange,
      submitOrder,
      buildCurrentPresetPayload,
      presetUnsupportedReason,
      hasTriggerCondition,
      hasExitCondition,
      markPriceSynced,
      syncPriceFromAtm,
      getRawValues,
      isSubmitting,
      hasUnsavedUserEdits,
    ]
  )
}
